import { useEffect, useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { Sparkles } from "@react-three/drei"
import * as THREE from "three"

// Sprint 6 — Orbe lumínico del Estado Cero.
//
// Versión expandida del orbe de void: tres capas de halo concéntricas
// respirando con frecuencias distintas, sparkles densas, dos anillos
// torus orbitando en ejes opuestos (recordando que la consciencia
// rota antes de tomar forma), un point light que tinta las estrellas
// cercanas con cyan suave, y aparición gradual con fade-in al montar
// (5s para llegar a opacidad plena — coincide con el ritmo de la intro
// narrativa).
//
// El orbe NO es decorativo. Está pensado como representación visual
// del Tripulante en estado pre-encarnado: "Yo soy tú mismo reflejado
// en la malla digital". Si el Tripulante mira al centro mientras lee
// la intro, debe sentir reconocimiento.

export function VoidOrb({ visible }: { visible: boolean }) {
    const coreRef = useRef<THREE.Mesh>(null)
    const halo1Ref = useRef<THREE.Mesh>(null)
    const halo2Ref = useRef<THREE.Mesh>(null)
    const ring1Ref = useRef<THREE.Mesh>(null)
    const ring2Ref = useRef<THREE.Mesh>(null)
    const groupRef = useRef<THREE.Group>(null)

    // Fade-in al montar. mountRef registra el momento de aparición;
    // useFrame interpola opacidad/escala del grupo durante 5s.
    const mountRef = useRef(0)
    useEffect(() => {
        if (visible) mountRef.current = performance.now()
    }, [visible])

    const baseColor = useMemo(() => new THREE.Color("#7BD7FF"), [])

    useFrame((state) => {
        if (!visible) return
        const t = state.clock.elapsedTime
        const elapsed = mountRef.current
            ? performance.now() - mountRef.current
            : 0
        const fade = Math.min(1, elapsed / 5000)

        // Group scale arranca chico y se expande al fade.
        if (groupRef.current) {
            const s = 0.6 + 0.4 * fade
            groupRef.current.scale.setScalar(s)
        }

        if (coreRef.current) {
            const breathing = 1 + Math.sin(t * 0.85) * 0.06
            coreRef.current.scale.setScalar(breathing)
            const m = coreRef.current.material as THREE.MeshBasicMaterial
            m.opacity = 0.95 * fade
        }
        if (halo1Ref.current) {
            const breathing = 1 + Math.cos(t * 0.7 + 1.2) * 0.12
            halo1Ref.current.scale.setScalar(breathing)
            const m = halo1Ref.current.material as THREE.MeshBasicMaterial
            m.opacity = 0.22 * fade
        }
        if (halo2Ref.current) {
            const breathing = 1 + Math.sin(t * 0.45 + 2.1) * 0.18
            halo2Ref.current.scale.setScalar(breathing)
            const m = halo2Ref.current.material as THREE.MeshBasicMaterial
            m.opacity = 0.1 * fade
        }
        if (ring1Ref.current) {
            ring1Ref.current.rotation.y += 0.004
            ring1Ref.current.rotation.x = Math.sin(t * 0.25) * 0.4
            const m = ring1Ref.current.material as THREE.MeshBasicMaterial
            m.opacity = 0.65 * fade
        }
        if (ring2Ref.current) {
            ring2Ref.current.rotation.z -= 0.003
            ring2Ref.current.rotation.x = Math.cos(t * 0.18) * 0.45
            const m = ring2Ref.current.material as THREE.MeshBasicMaterial
            m.opacity = 0.5 * fade
        }
    })

    if (!visible) return null

    return (
        // Sprint 6 v2 — Posicionado a y=4 para coincidir con el target
        // de cámara durante void (CameraRig.VOID_TARGET). El orbe
        // queda en el tercio superior del viewport, dejando espacio
        // libre debajo para el texto narrativo y el botón ENCARNAR.
        <group ref={groupRef} position={[0, 4, 0]}>
            {/* Core lumínico — el "punto" de consciencia. */}
            <mesh ref={coreRef}>
                <sphereGeometry args={[0.34, 36, 36]} />
                <meshBasicMaterial
                    color="#E6F8FF"
                    transparent
                    opacity={0}
                    depthWrite={false}
                />
            </mesh>

            {/* Halo interno — primera capa de glow. */}
            <mesh ref={halo1Ref}>
                <sphereGeometry args={[0.85, 32, 32]} />
                <meshBasicMaterial
                    color={baseColor}
                    transparent
                    opacity={0}
                    depthWrite={false}
                />
            </mesh>

            {/* Halo externo — segunda capa, más amplia y tenue. */}
            <mesh ref={halo2Ref}>
                <sphereGeometry args={[1.7, 32, 32]} />
                <meshBasicMaterial
                    color={baseColor}
                    transparent
                    opacity={0}
                    depthWrite={false}
                />
            </mesh>

            {/* Anillo orbital ecuatorial — fino, rotando lento. */}
            <mesh ref={ring1Ref}>
                <torusGeometry args={[1.05, 0.018, 12, 96]} />
                <meshBasicMaterial
                    color="#9FE5FF"
                    transparent
                    opacity={0}
                    depthWrite={false}
                />
            </mesh>

            {/* Anillo orbital polar — eje perpendicular. */}
            <mesh
                ref={ring2Ref}
                rotation={[Math.PI / 2, 0, 0]}
            >
                <torusGeometry args={[1.25, 0.012, 10, 80]} />
                <meshBasicMaterial
                    color="#7BD7FF"
                    transparent
                    opacity={0}
                    depthWrite={false}
                />
            </mesh>

            {/* Punto de luz cyan — proyecta el orbe sobre las estrellas
                cercanas y le da volumen sin necesidad de fog. */}
            <pointLight
                intensity={2.4}
                distance={9}
                color="#9FE5FF"
                decay={1.2}
            />

            {/* Sparkles densas — partículas en suspensión, dan la
                sensación de "campo de información" alrededor del orbe. */}
            <Sparkles
                count={64}
                scale={[3.6, 3.6, 3.6]}
                size={4}
                speed={0.18}
                color="#B8E8FF"
                opacity={0.85}
            />
        </group>
    )
}
